import { MobileAppMenuItem } from "@app/mobile-app/components";
import { useRewardTasksUnclaimedCount } from "@app/gamification/hooks/use-reward-tasks-unclaimed-count.hook";
import { useIsMobile } from "@core/shared/hooks";
import { URLS } from "@core/shared/utils";
import { Button } from "@design-system/components/Button";
import { BaseLink } from "@design-system/components/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@design-system/ui/dropdown-menu";
import { useIsLoggedIn } from "@framework/account/hooks";
import { useLogout } from "@framework/auth/hooks";
import { userAtom } from "@framework/config/atoms";
import { platformFeaturesAtom } from "@framework/shared/atoms";
import { trans, plainTrans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import {
  Award,
  BookOpen,
  CalendarCheck2,
  FileBarChart2,
  History,
  Lock,
  LogOut,
  Medal,
  ReceiptText,
  ShoppingBag,
  Sparkles,
  Trophy,
  User,
} from "lucide-react";
import { useState } from "react";
import { UserAvatar } from "../../UserAvatar";

const accountLinks = [
  {
    to: URLS.account.profile,
    icon: <User className="mr-2 h-4 w-4" />,
    label: "profile",
  },
  {
    to: URLS.lessons.myLessons,
    icon: <BookOpen className="mr-2 h-4 w-4" />,
    label: "grades.myLessons",
  },
  {
    to: URLS.assessments.history,
    icon: <History className="mr-2 h-4 w-4" />,
    label: "assessments.historyTitle",
  },
  {
    to: URLS.sessions.history,
    icon: <CalendarCheck2 className="mr-2 h-4 w-4" />,
    label: "sessionHistory.title",
  },
  {
    to: URLS.sessions.invoices,
    icon: <ReceiptText className="mr-2 h-4 w-4" />,
    label: "myInvoices.title",
  },
  {
    to: URLS.account.security,
    icon: <Lock className="mr-2 h-4 w-4" />,
    label: "account.security",
  },
];

const studentReportsLink = {
  to: URLS.reports.list,
  icon: <FileBarChart2 className="mr-2 h-4 w-4" />,
  label: "reports.navigation",
};

export function HeaderUserMenu({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const logout = useLogout();
  const isLoggedIn = useIsLoggedIn();
  const user = userAtom.useValue();
  const isMobile = useIsMobile();
  const location = useLocation();
  const platformFeatures = platformFeaturesAtom.useValue();
  const { unclaimedCount: unclaimedTasks } = useRewardTasksUnclaimedCount();
  const loginUrl = URLS.auth.loginWithReturnTo(
    `${location.pathname}${location.search}`,
  );
  const links = [
    ...accountLinks.slice(0, 1),
    ...(platformFeatures.gamification === true
      ? [
          {
            to: URLS.gamification,
            icon: <Sparkles className="mr-2 h-4 w-4" />,
            label: "gamification.title",
            badge: platformFeatures.tasks === true ? unclaimedTasks : 0,
          },
        ]
      : []),
    ...(user?.isStudent === true && platformFeatures.league === true
      ? [
          {
            to: URLS.league,
            icon: <Trophy className="h-4 w-4" />,
            label: "league.navigation",
          },
        ]
      : []),
    ...(user?.isStudent === true && platformFeatures.gamification === true
      ? [
          {
            to: URLS.goldStore,
            icon: <ShoppingBag className="h-4 w-4" />,
            label: "goldStore.navigation",
          },
        ]
      : []),
    ...(user?.isStudent === true && platformFeatures.gamification === true
      ? [
          {
            to: `${URLS.gamification}?section=achievements`,
            icon: <Medal className="h-4 w-4" />,
            label: "gamification.achievements.title",
          },
        ]
      : []),
    ...(user?.isStudent === true
      ? [
          {
            to: URLS.certificates.list,
            icon: <Award className="h-4 w-4" />,
            label: "certificates.title",
          },
        ]
      : []),
    ...accountLinks.slice(1, 4),
    ...(user?.isStudent === true && platformFeatures.studentReports === true
      ? [studentReportsLink]
      : []),
    ...accountLinks.slice(4),
  ];

  if (compact || isLoggedIn) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {isLoggedIn && user ? (
            <Button
              variant="ghost"
              className="relative flex h-10 max-w-[180px] items-center gap-2 overflow-hidden rounded-xl px-2.5 xl:max-w-[14rem] md:px-3"
              title={plainTrans("userMenu")}
            >
              <UserAvatar
                name={user.name}
                imageUrl={user.image?.url}
                seed={user.id}
              />
              <span className="truncate text-sm font-medium text-gray-700 dark:text-gray-200 md:block">
                {isMobile ? user.name.split(" ")[0] : user.name}
              </span>
              {unclaimedTasks > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute start-1.5 top-1.5 size-2.5 rounded-full border-2 border-white bg-rose-500 dark:border-slate-900"
                />
              ) : null}
            </Button>
          ) : (
            <Button variant="ghost" size="icon" title={plainTrans("userMenu")}>
              <User className="size-5" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {isLoggedIn ? (
            <>
              <DropdownMenuLabel className="rtl:text-right">
                {trans("myAccount")}
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                {links.map((link) => (
                  <HeaderUserMenuItem
                    key={link.to}
                    {...link}
                    label={trans(link.label)}
                    onClick={() => setOpen(false)}
                  />
                ))}
                <MobileAppMenuItem onSelect={() => setOpen(false)} />
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>{trans("logout")}</span>
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem asChild>
                <BaseLink to={loginUrl} className="w-full">
                  {trans("login")}
                </BaseLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <BaseLink to={URLS.auth.register} className="w-full">
                  {trans("createAccount")}
                </BaseLink>
              </DropdownMenuItem>
              <MobileAppMenuItem onSelect={() => setOpen(false)} />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      <div className="hidden items-center space-x-2 sm:flex">
        <BaseLink to={loginUrl}>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center"
            title={plainTrans("login")}
          >
            <User className="mr-2 h-4 w-4" />
            {trans("login")}
          </Button>
        </BaseLink>
        <BaseLink to={URLS.auth.register}>
          <Button variant="default" size="sm" title={plainTrans("createAccount")}>
            {trans("createAccount")}
          </Button>
        </BaseLink>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            title={plainTrans("userMenu")}
          >
            <User className="size-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem asChild>
            <BaseLink to={loginUrl} className="w-full" title={plainTrans("login")}>
              {trans("login")}
            </BaseLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <BaseLink
              to={URLS.auth.register}
              className="w-full"
              title={plainTrans("createAccount")}
            >
              {trans("createAccount")}
            </BaseLink>
          </DropdownMenuItem>
          <MobileAppMenuItem />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

function HeaderUserMenuItem({
  to,
  icon,
  label,
  badge = 0,
  onClick,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  onClick?: () => void;
}) {
  return (
    <DropdownMenuItem asChild>
      <BaseLink
        to={to}
        className="flex w-full items-center gap-2"
        title={label}
        onClick={onClick}
      >
        {icon}
        <span>{label}</span>
        {badge > 0 ? (
          <span className="ms-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-black leading-none text-white">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </BaseLink>
    </DropdownMenuItem>
  );
}
